import { useCallback, useMemo } from 'react'
import { useTheme } from '@mui/material'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import {
  TooltipComponent,
  GridComponent
} from 'echarts/components'

import { ResearchYearStats } from '../types'
import { numberFormat } from 'shared/util'

echarts.use([
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
])

type ResearchBarGraphProps = {
  yearsStats: ResearchYearStats
  selectedYear: number | null
  setSelectedYear: (year: number | null) => void
  height?: number | undefined
}
export const ResearchBarGraph = ({
  yearsStats,
  selectedYear,
  setSelectedYear,
  height
}: ResearchBarGraphProps) => {
  const theme = useTheme()
  const selectedItemStyle = useMemo(
    () => ({
      color: theme.palette.primary.main
    }),
    [theme]
  )
  const unSelectedItemStyle = useMemo(
    () => ({
      color: theme.palette.action.disabled
    }),
    [theme]
  )
  const onClickGraph = useCallback(
    (params?: {
      dataIndex?: number
    }) => {
      if (params?.dataIndex !== undefined) {
        const year = yearsStats.list[params.dataIndex]?.year
        if (year) {
          setSelectedYear(selectedYear === year ? null : year)
        }
      }
    },
    [selectedYear, setSelectedYear, yearsStats]
  )
  const costFormatter = useCallback(
    (params: { value: number }) => {
      return numberFormat(params.value)
    },
    []
  )
  return (yearsStats.maxCount > 0) ? (
    <ReactEChartsCore
      echarts={echarts}
      notMerge={true}
      onEvents={{
        click: onClickGraph
      }}
      option={{
        //animation: false,
        grid: {
          left: 0,
          right: 0,
          top: 20,
          bottom: 0,
        },
        xAxis: [{
          type: 'category',
          show: true,
          data: yearsStats.list.map(r => `${r.year}年`),
          axisLabel: {
            interval: 0,
            fontSize: 10
          },
          axisTick: {
            alignWithLabel: true
          }
        }],
        yAxis: [{
          type: 'value',
          min: 0,
          max: yearsStats.maxCount,
          show: false
        }, {
          type: 'value',
          min: 0,
          max: yearsStats.maxCost,
          show: false
        }],
        series: [{
          type: 'bar',
          yAxisIndex: 0,
          name: 'trialCountByYear',
          barGap: 0,
          large: true,
          barWidth: '95%',
          showBackground: true,
          backgroundStyle: {
            color: 'rgba(180, 180, 180, 0.2)'
          },
          label: {
            show: true,
            formatter: '{c}件'
          },
          data: yearsStats.list.map(r => ({
            key: r.year,
            value: r.count,
            itemStyle: (
              selectedYear === null ? undefined :
              r.year === selectedYear ? selectedItemStyle :
              unSelectedItemStyle
            )
          }))
        }, {
          type: 'line',
          yAxisIndex: 1,
          name: 'trialCostByYear',
          label: {
            show: true,
            position: 'top',
            formatter: costFormatter,
            fontSize: 10,
            distance: 2
          },
          data: yearsStats.list.map(r => ({
            key: r.year,
            value: r.cost,
            itemStyle: (
              selectedYear === null ? undefined :
              r.year === selectedYear ? selectedItemStyle :
              unSelectedItemStyle
            )
          }))
        }]
      }}
      opts={{
        renderer: 'svg',
        width: 'auto'
      }}
      style={{
        height
      }}
      theme="insite"
    />
  ) : <></>
}
